// Uzbek phone helpers. The UI fixes the +998 country code and the user types
// only the 9 national digits; these helpers keep the stored value in E.164 and
// gracefully absorb pasted / browser-autofilled full numbers.

export const UZ_DIAL_CODE = '998';
export const UZ_NATIONAL_LENGTH = 9;

/** Digits only, a redundant leading country code stripped, capped at 9. Mid-typing input is left alone. */
export function toUzNational(input: string): string {
  const raw = input ?? '';
  let digits = raw.replace(/\D/g, '');
  // Treat a leading 998 as the country code when the value is clearly an
  // international form (had a +) or has more digits than a national number.
  // The length guard alone would otherwise eat a bare national number that
  // legitimately starts with 998 (operator 99 + 8…), e.g. "998123456".
  const looksInternational = raw.includes('+') || digits.length > UZ_NATIONAL_LENGTH;
  if (looksInternational && digits.startsWith(UZ_DIAL_CODE)) {
    digits = digits.slice(UZ_DIAL_CODE.length);
  }
  return digits.slice(0, UZ_NATIONAL_LENGTH);
}

/** Compose the E.164 value the API expects, or '' when there are no digits. */
export function toUzE164(input: string): string {
  const national = toUzNational(input);
  return national ? `+${UZ_DIAL_CODE}${national}` : '';
}

/** True when the value is a complete Uzbek E.164 number (+998 + 9 digits). */
export function isCompleteUzPhone(e164: string): boolean {
  return new RegExp(`^\\+${UZ_DIAL_CODE}\\d{${UZ_NATIONAL_LENGTH}}$`).test(e164);
}

/** Shared zod-friendly pattern for a complete Uzbek number in E.164. */
export const UZ_E164_PATTERN = new RegExp(`^\\+${UZ_DIAL_CODE}\\d{${UZ_NATIONAL_LENGTH}}$`);
